import React from 'react';
import { FaFilter, FaUser } from 'react-icons/fa';
import { Note } from '../types';
import { formatTimestamp } from '../utils/formatTimestamp';
import NoteImage from './NoteImage';
import Button from './ui/Button';
import { noteAuthor } from '../utils/noteAuthor';

interface NoteHoverCardProps {
  note: Note;
  deviceColor: string;
  onImageClick: (noteId: number) => void;
}

const NoteHoverCard: React.FC<NoteHoverCardProps> = ({ note, deviceColor, onImageClick }) => {
  const firstImage = note.images?.[0];
  const additionalImageCount = Math.max((note.images?.length ?? 0) - 1, 0);
  return (
    <div className="w-full rounded-md border border-border bg-white p-3 text-xs shadow-lg">
      <div className="flex items-center gap-2 mb-2 text-gray-600">
        <span
          className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: deviceColor }}
          aria-hidden
        />
        <span className="font-medium">{formatTimestamp(note.created_at)}</span>
        {note.filter_changed && (
          <span className="flex items-center text-foreground">
            <FaFilter className="mr-1" /> Filter Changed
          </span>
        )}
      </div>
      <div className="flex items-center text-muted-foreground mb-2">
        <FaUser className="mr-1" />
        <span className="min-w-0 break-all">{noteAuthor(note)}</span>
      </div>
      <p
        className="whitespace-pre-wrap text-gray-800 mb-2"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 6,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {note.note_text}
      </p>
      {firstImage && (
        <Button
          variant="link"
          title="Open this note's attached image"
          aria-label="Open note image"
          onClick={(e) => {
            e.stopPropagation();
            onImageClick(note.id);
          }}
          className="relative inline-block"
        >
          <NoteImage
            noteId={note.id}
            imageId={firstImage.id}
            alt="Note attachment"
            className="max-h-[120px] w-auto rounded-sm cursor-pointer border border-border"
          />
          {additionalImageCount > 0 && (
            <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-sm">
              +{additionalImageCount} more
            </span>
          )}
        </Button>
      )}
    </div>
  );
};

export default NoteHoverCard;
